export type BackendReachability = boolean | null;

export interface ConnectivityState {
  /** Browser's network signal. */
  browserOnline: boolean;
  /** null means no backend request has completed in this page session. */
  backendReachable: BackendReachability;
}

type ConnectivityListener = (state: ConnectivityState) => void;

const listeners = new Set<ConnectivityListener>();

const initialBrowserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
let state: ConnectivityState = {
  browserOnline: initialBrowserOnline,
  backendReachable: initialBrowserOnline ? null : false,
};

const publish = (next: ConnectivityState): void => {
  if (next.browserOnline === state.browserOnline && next.backendReachable === state.backendReachable) {
    return;
  }
  state = next;
  listeners.forEach((listener) => listener(state));
};

export const getConnectivityState = (): ConnectivityState => state;

export const subscribeConnectivity = (listener: ConnectivityListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const markBrowserOffline = (): void => {
  publish({ browserOnline: false, backendReachable: false });
};

export const markBrowserOnline = (): void => {
  // A browser reconnect does not prove that the Cloud Run service is reachable.
  // Reset to unknown so the next intentional request is the probe that can wake
  // a scaled-to-zero service.
  publish({ browserOnline: true, backendReachable: null });
};

export const markBackendReachable = (): void => {
  if (!state.browserOnline) return;
  publish({ ...state, backendReachable: true });
};

export const markBackendUnavailable = (): void => {
  publish({ ...state, backendReachable: false });
};

export const isBackendRequestAvailable = (): boolean => (
  state.browserOnline && state.backendReachable !== false
);
